/**
 * Copyright © 2026 Adnuntius AS.
 */
import {SignalResourceListBindingService} from '../common/controller/signal-resource-list-binding.service';
import {DestroyRef, Component, Inject, inject, signal} from '@angular/core';
import * as _ from 'lodash';
import template from './external-ad-unit-edit-page.component.html';
import {SignalAdminResourceEditPageBase} from '../common/controller/signal-admin-resource-edit-page-base';
import {AdUnitApiService} from '../ad-unit/ad-unit-api.service';
import {CreativeApiService} from '../creative/creative-api.service';
import {ExternalDemandSourceApiService} from '../admin/external-demand-source/external-demand-source-api.service';
import {ExternalAdUnitApiService, ExternalAdUnitModel} from './external-ad-unit-api.service';
import {FormsModule} from '@angular/forms';
import {TranslateModule} from '@ngx-translate/core';
import {UIRouterModule} from '@uirouter/angular';
import {APP_SHARED} from '../common/app-shared';
import {ADMIN_RESOURCE_PAGES} from '../common/controller/admin-resource-pages';
import {TARGETING_COMPONENTS} from '../targeting/targeting-components';
import {ExternalAdUnitEditUsageComponent} from './external-ad-unit-edit-usage.component';

@Component({
  standalone: true,
  imports: [
    ...APP_SHARED,
    FormsModule,
    TranslateModule,
    UIRouterModule,
    ...ADMIN_RESOURCE_PAGES,
    ...TARGETING_COMPONENTS,
    ExternalAdUnitEditUsageComponent
  ],
  selector: 'adn-external-ad-unit-edit-page',
  template
})
export class ExternalAdUnitEditPageComponent extends SignalAdminResourceEditPageBase<ExternalAdUnitModel> {
  private readonly listLifetime = inject(DestroyRef);
  readonly externalAdTypes = ['HTML', 'TEXT', 'VIDEO', 'FLASH', 'IMAGE', 'MIXED'];

  private readonly extDemandSourcesState = signal<any[] | false>(false);
  get extDemandSources(): any[] | false { return this.extDemandSourcesState(); }
  set extDemandSources(value: any[] | false) { this.extDemandSourcesState.set(value); }

  usageResource: any = {
    creativeList: {}
  };

  private listInitialized = false;

  constructor(
    private externalDemandSourceApi: ExternalDemandSourceApiService,
    private adUnitApi: AdUnitApiService,
    @Inject(CreativeApiService) private Creative: CreativeApiService,
    private listBinding: SignalResourceListBindingService
  ) {
    super(inject(ExternalAdUnitApiService), 'externalAdUnit');

    this.externalDemandSourceApi.listOptions().subscribe((page) => {
      this.extDemandSources = page.results || [];
      this.applyDefaultDemandSource();
      this.resolveDemandSourceReference();
    });
  }

  protected override afterEditingModelReset(): void {
    this.editingModel.dimensions = _.isArray(this.editingModel.dimensions) ? this.editingModel.dimensions : [[]];
    this.editingModel.dimensions[0] = _.isArray(this.editingModel.dimensions[0]) ? this.editingModel.dimensions[0] : [];
    this.applyDefaultDemandSource();
    this.resolveDemandSourceReference();
    if (this.editingModel.id && !this.listInitialized) {
      this.listInitialized = true;
      const moreParams = {externalAdUnit: this.editingModel.id};
      this.usageResource = this.listBinding.create(this.adUnitApi, 'fullAdUnitsForExternalAdUnit', this.listLifetime, {
      baseTable: 'adUnit',
        moreParams,
        noInitQuery: true,
      }, this.usageResource);
      this.usageResource.creativeList = this.listBinding.create(this.Creative, 'fullCreativesForExternalAdUnit', this.listLifetime, {
      baseTable: 'creative',
        moreParams,
        noInitQuery: true
      }, this.usageResource.creativeList);
      if (!this.isNew) {
        this.usageResource.makeQuery?.();
        this.usageResource.creativeList.makeQuery?.();
      }
    }
    if (!this.isNew) {
      this.usageResource.makeQuery?.();
      this.usageResource.creativeList.makeQuery?.();
    }
  }

  protected override prepareForSubmit(model: ExternalAdUnitModel): void {
    // Required for API backwards compatibility with singular width/height fields.
    delete model.width;
    delete model.height;
  }

  addDimensions(): void {
    this.editingModel.dimensions = this.editingModel.dimensions || [];
    this.editingModel.dimensions.push([]);
    this.markExternalFormDirty();
  }

  removeDimensions(index: number): void {
    _.pullAt(this.editingModel.dimensions, index);
    this.markExternalFormDirty();
  }

  onDemandSourceChange(): void {
    this.resolveDemandSourceReference();
    this.markExternalFormDirty();
  }

  private applyDefaultDemandSource(): void {
    if (!_.isArray(this.extDemandSources) || this.extDemandSources.length !== 1) {
      return;
    }
    if (!this.editingModel.externalDemandSource) {
      this.editingModel.externalDemandSource = this.extDemandSources[0];
    }
  }

  private resolveDemandSourceReference(): void {
    if (!_.isArray(this.extDemandSources) || !this.editingModel?.externalDemandSource) {
      return;
    }
    const extDemSourceId = _.get(this.editingModel, ['externalDemandSource', 'id'])
      || (_.isString(this.editingModel.externalDemandSource) ? this.editingModel.externalDemandSource : null);
    if (!extDemSourceId) {
      return;
    }
    const fullExtDemandSource = _.find(this.extDemandSources, ['id', extDemSourceId]);
    if (fullExtDemandSource) {
      this.editingModel.externalDemandSource = fullExtDemandSource;
    }
  }
}
